/**
 * Core primitives, styled to the approved design exploration.
 *
 * Colour comes only from the CSS custom properties in `src/design/tokens.css`,
 * so all three dark variants (graphite, slate, ash) work without a rebuild.
 * Geometry and type come from the measured scales in `src/design`.
 */
import type { CSSProperties, ReactNode, ButtonHTMLAttributes, AnchorHTMLAttributes } from "react";
import { fontSize, radius, font, type Tone } from "@design/index";
import { formatMoney, type Money as MoneyValue } from "@contracts/money";

/* ------------------------------------------------------------------ Button */

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

const BUTTON_PADDING: Record<ButtonSize, string> = {
  sm: "7px 12px",
  md: "10px 16px",
  lg: "13px 22px",
};
const BUTTON_TEXT: Record<ButtonSize, number> = {
  sm: fontSize.body,
  md: fontSize.bodyLg,
  lg: fontSize.base,
};

function buttonStyle(size: ButtonSize, block: boolean): CSSProperties {
  const base: CSSProperties = {
    display: block ? "flex" : "inline-flex",
    width: block ? "100%" : undefined,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: BUTTON_PADDING[size],
    fontSize: BUTTON_TEXT[size],
    fontWeight: 600,
    lineHeight: 1.2,
    borderRadius: radius.control,
    border: "1px solid transparent",
    textDecoration: "none",
    whiteSpace: "nowrap",
  };
  return base;
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
};

export function Button({
  variant = "secondary", size = "md", block = false, style, className, children, ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      className={["cm-btn", `cm-btn-${variant}`, className].filter(Boolean).join(" ")}
      style={{ ...buttonStyle(size, block), ...style }}
    >
      {children}
    </button>
  );
}

type LinkButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
};

export function LinkButton({
  variant = "secondary", size = "md", block = false, style, className, children, ...rest
}: LinkButtonProps) {
  return (
    <a
      {...rest}
      className={["cm-btn", `cm-btn-${variant}`, className].filter(Boolean).join(" ")}
      style={{ ...buttonStyle(size, block), ...style }}
    >
      {children}
    </a>
  );
}

/* -------------------------------------------------------------------- Card */

export function Card({
  children, padding = 20, style, lift = false,
}: { children: ReactNode; padding?: number; style?: CSSProperties; lift?: boolean }) {
  return (
    <div
      {...(lift ? { "data-lift": "" } : {})}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: radius.card,
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------- Badge */

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        borderRadius: radius.pill,
        background: `var(--tone-${tone}-bg)`,
        color: `var(--tone-${tone}-fg)`,
        fontSize: fontSize.label,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- Eyebrow */

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: font.mono,
        fontSize: fontSize.label,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--muted)",
      }}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- Money */

/**
 * Renders an amount with its currency, in tabular figures so columns align.
 * `asOf` is rendered when supplied because SRS §14 requires financial values
 * to carry as-of information wherever they are not live.
 */
export function Money({
  value, size = fontSize.bodyLg, asOf, emphasis = false,
}: { value: MoneyValue; size?: number; asOf?: string; emphasis?: boolean }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontFamily: font.mono,
          fontSize: size,
          fontWeight: emphasis ? 600 : 500,
          fontVariantNumeric: "tabular-nums",
          color: emphasis ? "var(--text)" : "var(--text-2)",
        }}
      >
        {formatMoney(value)}
      </span>
      {asOf ? (
        <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>as of {asOf}</span>
      ) : null}
    </span>
  );
}

/* -------------------------------------------------------------- StateBlock */

export type StateKind = "loading" | "empty" | "error" | "forbidden" | "stale";

const STATE_TONE: Record<StateKind, Tone> = {
  loading: "neutral",
  empty: "neutral",
  error: "danger",
  forbidden: "warn",
  stale: "warn",
};

/**
 * The non-happy-path block. Every list and panel in the delivery plan owes
 * loading / empty / error / forbidden / stale states, so they get one
 * component rather than ad-hoc markup that drifts.
 */
export function StateBlock({
  kind, title, detail, action,
}: { kind: StateKind; title: string; detail?: string; action?: ReactNode }) {
  return (
    <div
      role={kind === "error" ? "alert" : undefined}
      style={{
        display: "grid",
        gap: 10,
        justifyItems: "center",
        textAlign: "center",
        padding: "40px 24px",
        background: "var(--surface)",
        border: "1px dashed var(--line-strong)",
        borderRadius: radius.card,
      }}
    >
      <Badge tone={STATE_TONE[kind]}>{kind}</Badge>
      <p style={{ fontSize: fontSize.base, fontWeight: 600, color: "var(--text)" }}>{title}</p>
      {detail ? (
        <p style={{ fontSize: fontSize.body, color: "var(--muted)", maxWidth: 440 }}>{detail}</p>
      ) : null}
      {action}
    </div>
  );
}

/* ------------------------------------------------------------- PageHeader */

export function PageHeader({
  eyebrow, title, description, actions,
}: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return (
    <header
      className="cm-page-header"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "flex-end",
        justifyContent: "space-between",
        paddingBottom: 20,
        borderBottom: "1px solid var(--line)",
      }}
    >
      <div style={{ display: "grid", gap: 6 }}>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <h1 style={{ fontSize: 28, fontWeight: 750, letterSpacing: "-0.03em", lineHeight: 1.1 }}>
          {title}
        </h1>
        {description ? (
          <p style={{ fontSize: fontSize.bodyLg, color: "var(--muted)", maxWidth: 620 }}>
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div style={{ display: "flex", gap: 10 }}>{actions}</div> : null}
    </header>
  );
}

/* ---------------------------------------------------------------- StatTile */

export function StatTile({
  label, value, hint, tone,
}: { label: string; value: ReactNode; hint?: string; tone?: Tone }) {
  return (
    <Card padding={16} style={{ display: "grid", gap: 8, alignContent: "start" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <Eyebrow>{label}</Eyebrow>
        {tone ? <span style={{ width: 6, height: 6, borderRadius: radius.pill,
                                background: `var(--tone-${tone}-fg)` }} /> : null}
      </div>
      <div style={{ fontSize: fontSize.h2, fontWeight: 700, color: "var(--text)" }}>{value}</div>
      {hint ? <p style={{ fontSize: fontSize.label, color: "var(--faint)" }}>{hint}</p> : null}
    </Card>
  );
}
